/**
 * Stockout Alert Card Component
 * ==============================
 * Displays a single stockout prediction with details
 * Includes "Why?" drill-down for understanding factors
 */

import React, { useState } from 'react';
import { AlertTriangle, TrendingDown, Package, Warehouse, ChevronDown, ChevronUp, HelpCircle, Bot } from 'lucide-react';
import { useLocation } from 'wouter';
import { StockoutPrediction } from '../hooks/useStockoutPredictions';

interface StockoutAlertCardProps {
  prediction: StockoutPrediction;
  onClick?: () => void;
}

// Generate "Why" context based on prediction data
function generateWhyContext(prediction: StockoutPrediction): {
  factors: string[];
  explanation: string;
  suggestedAction: string;
} {
  const factors: string[] = [];
  let explanation = '';
  let suggestedAction = '';
  
  const wos = prediction.current_state.store_wos;
  const oosRisk = prediction.predicted_oos_pct;
  const velocity = prediction.current_state.avg_weekly_units || 0;
  
  // Determine contributing factors
  if (wos !== null && wos < 1) {
    factors.push('critically_low_wos');
    explanation = `Only ${wos.toFixed(1)} days of stock remaining`;
    suggestedAction = 'Expedite replenishment order or emergency DC transfer';
  } else if (wos !== null && wos < 2) {
    factors.push('low_wos');
    explanation = `Low weeks of supply (${wos.toFixed(1)}w) with current demand`;
    suggestedAction = 'Place immediate replenishment order';
  } else if (velocity > 100) {
    factors.push('high_velocity');
    explanation = `High sales velocity (${velocity.toFixed(0)} units/week) depleting stock`;
    suggestedAction = 'Increase reorder quantity and frequency';
  } else {
    factors.push('inventory_imbalance');
    explanation = `Predicted ${oosRisk.toFixed(0)}% stockout probability based on demand patterns`;
    suggestedAction = 'Review inventory allocation and reorder point';
  }
  
  // Add DC inventory factor if available
  if (prediction.current_state.dc_wos !== null && prediction.current_state.dc_wos < 1) {
    factors.push('low_dc_inventory');
    if (!explanation.includes('DC')) {
      explanation += `. DC also running low (${prediction.current_state.dc_wos.toFixed(1)}w supply)`;
    }
  }
  
  return { factors, explanation, suggestedAction };
}

export default function StockoutAlertCard({ prediction, onClick }: StockoutAlertCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [, setLocation] = useLocation();
  
  const whyContext = generateWhyContext(prediction);
  const getRiskIcon = () => {
    switch (prediction.risk_level) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Package className="w-5 h-5 text-green-500" />;
    }
  };

  const getRiskColor = () => {
    switch (prediction.risk_level) {
      case 'critical':
        return 'border-red-300 bg-white dark:bg-red-950/20';
      case 'warning':
        return 'border-yellow-300 bg-white dark:bg-yellow-950/20';
      default:
        return 'border-border bg-white';
    }
  };

  const getRiskBadge = () => {
    switch (prediction.risk_level) {
      case 'critical':
        return (
          <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
            Critical
          </span>
        );
      case 'warning':
        return (
          <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-medium rounded">
            Warning
          </span>
        );
      default:
        return null;
    }
  };

  const handleWhyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  
  const handleAskAgent = (e: React.MouseEvent) => {
    e.stopPropagation();
    const question = `Why is ${prediction.product_name || prediction.upc} at ${prediction.predicted_oos_pct.toFixed(0)}% stockout risk? What should I do?`;
    const url = `/agent-hub?q=${encodeURIComponent(question)}&agent=inventory_risk_replenishment&forceAgent=true&submit=true`;
    setLocation(url);
  };

  return (
    <div
      className={`border rounded-lg p-3 transition-all hover:shadow-md ${getRiskColor()} ${
        onClick ? 'cursor-pointer' : ''
      } ${isExpanded ? '' : 'aspect-square'} flex flex-col justify-between`}
      onClick={onClick}
    >
      {/* Product Name + Badge */}
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-xs text-foreground leading-tight line-clamp-2 flex-1 pr-1">
          {prediction.product_name || prediction.upc}
        </h3>
        {getRiskBadge()}
      </div>

      {/* Main Metric */}
      <div className="mb-2">
        <div className="text-3xl font-bold text-foreground">{prediction.predicted_oos_pct.toFixed(0)}%</div>
        <div className="text-xs text-muted-foreground">Stockout Risk</div>
      </div>

      {/* Bottom Metrics - Compact Grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-auto">
        <div>
          <span className="text-muted-foreground">WOS:</span>
          <span className="font-semibold text-foreground ml-1">
            {prediction.current_state.store_wos ? `${prediction.current_state.store_wos.toFixed(1)}w` : '-'}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Risk:</span>
          <span className="font-semibold text-foreground ml-1">
            ${prediction.revenue_at_risk.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>
      
      {/* Why? Button */}
      <button
        onClick={handleWhyClick}
        className="mt-2 flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium py-1 px-2 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors w-full"
      >
        <HelpCircle size={12} />
        <span>Why?</span>
        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      
      {/* Expanded Why Section */}
      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
          {/* Factors */}
          <div className="flex flex-wrap gap-1">
            {whyContext.factors.map((factor, idx) => (
              <span 
                key={idx}
                className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-800 border border-amber-200"
              >
                {factor.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
          
          {/* Explanation */}
          <p className="text-xs text-gray-700 leading-relaxed">
            {whyContext.explanation}
          </p>
          
          {/* Suggested Action */}
          <div className="bg-green-50 border border-green-200 rounded p-2">
            <p className="text-xs text-green-800">
              <span className="font-semibold">Action:</span> {whyContext.suggestedAction}
            </p>
          </div>
          
          {/* Ask Agent Button */}
          <button
            onClick={handleAskAgent}
            className="flex items-center justify-center gap-1.5 w-full py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            <Bot size={12} />
            <span>Ask Agent for Details</span>
          </button>
        </div>
      )}
    </div>
  );
}

